import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

/**
 * Command Handler for Gantt Chart components.
 *
 * Handles commands for:
 * - Time scale switching (day/week/month/quarter)
 * - Show/hide dependencies, milestones, progress, critical path, iteration bands
 * - Grouping by field
 * - Row height / bar height adjustments
 * - Bar drag/resize enable/disable
 * - Scroll to today
 */
export class GanttChartCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // 1. Set time scale: "set time scale to week"
    this.commandPatterns.set(
      /set (?:time ?)?scale (?:to )?(day|week|month|quarter)/i,
      (match) => ({ properties: { timeScale: match[1].toLowerCase() } })
    );

    // 2. Show dependencies: "show dependencies"
    this.commandPatterns.set(
      /show dep(?:endenc(?:y|ies))?/i,
      () => ({ properties: { showDependencies: true } })
    );

    // 3. Hide dependencies: "hide dependencies"
    this.commandPatterns.set(
      /hide dep(?:endenc(?:y|ies))?/i,
      () => ({ properties: { showDependencies: false } })
    );

    // 4. Show/hide milestones
    this.commandPatterns.set(
      /show milestones?/i,
      () => ({ properties: { showMilestones: true } })
    );
    this.commandPatterns.set(
      /hide milestones?/i,
      () => ({ properties: { showMilestones: false } })
    );

    // 5. Show/hide progress
    this.commandPatterns.set(
      /show progress/i,
      () => ({ properties: { showProgress: true } })
    );
    this.commandPatterns.set(
      /hide progress/i,
      () => ({ properties: { showProgress: false } })
    );

    // 6. Show/hide critical path
    this.commandPatterns.set(
      /show critical (?:path|chain)/i,
      () => ({ properties: { showCriticalPath: true } })
    );
    this.commandPatterns.set(
      /hide critical (?:path|chain)/i,
      () => ({ properties: { showCriticalPath: false } })
    );

    // 7. Show/hide iteration bands
    this.commandPatterns.set(
      /show (?:iteration|sprint) bands?/i,
      () => ({ properties: { showIterationBands: true } })
    );
    this.commandPatterns.set(
      /hide (?:iteration|sprint) bands?/i,
      () => ({ properties: { showIterationBands: false } })
    );

    // 8. Show/hide today line
    this.commandPatterns.set(
      /show today(?: line)?/i,
      () => ({ properties: { showTodayLine: true } })
    );
    this.commandPatterns.set(
      /hide today(?: line)?/i,
      () => ({ properties: { showTodayLine: false } })
    );

    // 9. Show/hide grid lines
    this.commandPatterns.set(
      /show grid(?: ?lines)?/i,
      () => ({ properties: { showGridLines: true } })
    );
    this.commandPatterns.set(
      /hide grid(?: ?lines)?/i,
      () => ({ properties: { showGridLines: false } })
    );

    // 10. Group by field: "group by team" or "group by assignee"
    this.commandPatterns.set(
      /group (?:by |tasks by )(.+)/i,
      (match, component) => {
        const field = match[1].trim().toLowerCase();
        const fm = (component.properties?.fieldMapping ?? {}) as Record<string, string | undefined>;
        return { properties: { fieldMapping: { ...fm, group: field } } };
      }
    );

    // 11. Set row height: "set row height 40" or "set row height to 40"
    this.commandPatterns.set(
      /set row height (?:to )?(\d+)/i,
      (match) => ({ properties: { rowHeight: Number(match[1]) } })
    );

    // 12. Set bar height: "set bar height 28"
    this.commandPatterns.set(
      /set bar height (?:to )?(\d+)/i,
      (match) => ({ properties: { barHeight: Number(match[1]) } })
    );

    // 13. Enable/disable bar drag
    this.commandPatterns.set(
      /enable (?:bar )?drag/i,
      () => ({ properties: { barDragEnabled: true } })
    );
    this.commandPatterns.set(
      /disable (?:bar )?drag/i,
      () => ({ properties: { barDragEnabled: false } })
    );

    // 14. Enable/disable bar resize
    this.commandPatterns.set(
      /enable (?:bar )?resize/i,
      () => ({ properties: { barResizeEnabled: true } })
    );
    this.commandPatterns.set(
      /disable (?:bar )?resize/i,
      () => ({ properties: { barResizeEnabled: false } })
    );

    // 15. Show/hide export button
    this.commandPatterns.set(
      /show export(?: button)?/i,
      () => ({ properties: { showExportButton: true } })
    );
    this.commandPatterns.set(
      /hide export(?: button)?/i,
      () => ({ properties: { showExportButton: false } })
    );

    // 16. Show/hide undated items
    this.commandPatterns.set(
      /show undated/i,
      () => ({ properties: { showUndatedItems: true } })
    );
    this.commandPatterns.set(
      /hide undated/i,
      () => ({ properties: { showUndatedItems: false } })
    );

    // 17. Set label column width: "set label width 250"
    this.commandPatterns.set(
      /set label (?:column )?width (?:to )?(\d+)/i,
      (match) => ({ properties: { labelColumnWidth: Number(match[1]) } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.GANTT_CHART) return false;
    const keywords = [
      "gantt", "time scale", "timescale", "scale", "dependencies", "dependency",
      "milestone", "progress", "critical path", "iteration", "sprint",
      "today", "grid", "group by", "row height", "bar height",
      "drag", "resize", "export", "undated", "label width",
    ];
    return keywords.some(k => command.toLowerCase().includes(k));
  }

  async parseCommand(
    command: string,
    component: Component,
  ): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    return {
      success: false,
      error: "Could not understand the gantt chart command",
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'set time scale to day',
      'set time scale to week',
      'set time scale to month',
      'set time scale to quarter',
      'show dependencies',
      'hide dependencies',
      'group by team',
      'show critical path',
      'show iteration bands',
      'enable bar drag',
      'show export button',
      'set row height 40',
      'show milestones',
      'show progress',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const props = component.properties || {};
    const suggestions: string[] = [];

    const currentScale = String(props.timeScale || 'week');
    const scales = ['day', 'week', 'month', 'quarter'].filter(s => s !== currentScale);
    suggestions.push(`set time scale to ${scales[0]}`);

    if (props.showDependencies === false) {
      suggestions.push('show dependencies');
    } else {
      suggestions.push('hide dependencies');
    }

    if (!props.showCriticalPath) {
      suggestions.push('show critical path');
    }

    if (!props.showIterationBands) {
      suggestions.push('show iteration bands');
    }

    if (!props.barDragEnabled) {
      suggestions.push('enable bar drag');
    } else {
      suggestions.push('disable bar drag');
    }

    suggestions.push('group by assignee');

    return suggestions;
  }
}

export const ganttChartCommandHandler = new GanttChartCommandHandler();
